import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Plus, Search, Trash2, FileSpreadsheet, ChevronLeft, ChevronRight, Download, UserCheck, Clock, CheckCircle, FileText, CheckSquare, FolderInput, ArrowRightLeft, XCircle, Ban } from 'lucide-react';
import Layout from '@/components/Layout';
import ClientCard from '@/components/ClientCard';
import AddClientModal from '@/components/AddClientModal';
import EditClientModal from '@/components/EditClientModal';
import BatchUploadModal from '@/components/BatchUploadModal';
import StatusDetailModal from '@/components/StatusDetailModal';
import ExportExcelModal from '@/components/ExportExcelModal';
import MoveToPeriodModal from '@/components/MoveToPeriodModal';
import MigrateClientsModal from '@/components/MigrateClientsModal';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 12;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const ClientsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showBatchUploadModal, setShowBatchUploadModal] = useState(false);
  const [showStatusDetailModal, setShowStatusDetailModal] = useState(false);
  const [selectedClientForStatus, setSelectedClientForStatus] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showMovePeriodModal, setShowMovePeriodModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: '', name: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, selectedStatus, selectedPaymentStatus, selectedPartner, startDate, endDate]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/clients${selectedPeriod !== 'all' ? `?period_id=${selectedPeriod}` : ''}`, getAuthHeaders())
      ];
      
      if (isAdmin) {
        requests.push(axios.get(`${API}/periods`, getAuthHeaders()));
        requests.push(axios.get(`${API}/users/partners`, getAuthHeaders()));
        requests.push(axios.get(`${API}/stats`, getAuthHeaders()));
      }
      
      const results = await Promise.all(requests);
      setClients(results[0].data);
      
      if (isAdmin) {
        setPeriods(results[1].data);
        // Ordenar parceiros por nome
        const sortedPartners = results[2].data.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setPartners(sortedPartners);
        setStats(results[3].data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    setCurrentPage(1);
    let filtered = [...clients];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.nome_completo?.toLowerCase().includes(search) ||
        c.razao_social?.toLowerCase().includes(search) ||
        c.cpf?.includes(searchTerm) ||
        c.cnpj?.includes(searchTerm) ||
        c.email?.toLowerCase().includes(search)
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(c => c.status === selectedStatus);
    }

    if (selectedPaymentStatus !== 'all') {
      filtered = filtered.filter(c => c.payment_status === selectedPaymentStatus);
    }

    if (selectedPartner !== 'all') {
      filtered = filtered.filter(c => c.created_by === selectedPartner);
    }

    // Filtro por data
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(c => new Date(c.created_at) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(c => new Date(c.created_at) <= end);
    }

    setFilteredClients(filtered);
  };

  const handleClientClick = (client) => {
    if (isAdmin) {
      navigate(`/client/${client.id}`);
    }
    // Parceiro não tem ação ao clicar no card - deve usar o ícone de edição
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowEditModal(true);
  };

  const handleDeleteClient = async (clientId) => {
    // Find client to show name in confirmation
    const client = clients.find(c => c.id === clientId);
    const clientName = client?.nome_completo || client?.razao_social || 'Cliente';
    
    setDeleteTarget({
      type: 'single',
      id: clientId,
      name: clientName
    });
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedClients.length === 0) return;
    
    setDeleteTarget({
      type: 'batch',
      id: selectedClients,
      name: `${selectedClients.length} cliente(s)`
    });
    setShowDeleteConfirmModal(true);
  };

  const executeDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'single') {
        await axios.delete(`${API}/clients/${deleteTarget.id}`, getAuthHeaders());
        toast.success('Cliente excluído com sucesso');
      } else if (deleteTarget.type === 'batch') {
        await axios.delete(`${API}/clients/batch`, {
          ...getAuthHeaders(),
          data: deleteTarget.id
        });
        toast.success('Clientes excluídos com sucesso');
        setSelectedClients([]);
      }
      fetchData();
      setShowDeleteConfirmModal(false);
    } catch (error) {
      toast.error('Erro ao excluir cliente(s)');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePaymentStatusChange = async (clientId, newStatus) => {
    try {
      await axios.put(`${API}/clients/${clientId}/payment-status?payment_status=${newStatus}`, {}, getAuthHeaders());
      toast.success('Status de pagamento atualizado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPeriod('all');
    setSelectedStatus('all');
    setSelectedPaymentStatus('all');
    setSelectedPartner('all');
    setStartDate('');
    setEndDate('');
  };

  const activePeriod = periods.find(p => p.status === 'ativo');
  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const paginatedClients = filteredClients.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Calcular estatísticas dos clientes filtrados
  const filteredStats = {
    total: filteredClients.length,
    protocolo: filteredClients.filter(c => c.status === 'protocolo').length,
    em_processo: filteredClients.filter(c => c.status === 'em_processo_baixa').length,
    concluido: filteredClients.filter(c => c.status === 'concluido').length,
    revogada: filteredClients.filter(c => c.status === 'revogada').length,
    cancelado: filteredClients.filter(c => c.status === 'cancelado').length
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              {isAdmin ? 'Todos os Clientes' : 'Meus Clientes'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {filteredClients.length} cliente(s) encontrado(s)
            </p>
            {activePeriod && <Badge variant="outline" className="mt-1">Período Ativo: {activePeriod.name}</Badge>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={() => setShowBatchUploadModal(true)}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Upload em Lote
                </Button>
                <Button variant="outline" onClick={() => setShowExportModal(true)}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
              </>
            )}
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-2 ${isAdmin ? 'md:grid-cols-4 lg:grid-cols-7' : 'md:grid-cols-3 lg:grid-cols-5'} gap-4 mb-6`}>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{isAdmin ? (stats?.total_clients || filteredStats.total) : filteredStats.total}</p>
                  <p className="text-xs text-muted-foreground">Total de Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {isAdmin && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_partners || partners.length}</p>
                    <p className="text-xs text-muted-foreground">Parceiros</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{isAdmin ? (stats?.protocolo || filteredStats.protocolo) : filteredStats.protocolo}</p>
                  <p className="text-xs text-muted-foreground">Protocolo</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{isAdmin ? (stats?.em_processo_baixa || filteredStats.em_processo) : filteredStats.em_processo}</p>
                  <p className="text-xs text-muted-foreground">Em Processo</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{isAdmin ? (stats?.completed_services ?? filteredStats.concluido) : filteredStats.concluido}</p>
                  <p className="text-xs text-muted-foreground">Concluídos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stats-card-revogadas">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{isAdmin ? (stats?.revogada_services ?? filteredStats.revogada) : filteredStats.revogada}</p>
                  <p className="text-xs text-muted-foreground">Revogadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {isAdmin && (
            <Card data-testid="stats-card-cancelados">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-200 rounded-lg">
                    <Ban className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.cancelado_services ?? filteredStats.cancelado}</p>
                    <p className="text-xs text-muted-foreground">Cancelados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, CPF/CNPJ, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Period Filter - Admin Only */}
              {isAdmin && (
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os períodos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os períodos</SelectItem>
                    {periods.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Status Filter */}
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="protocolo">Protocolo</SelectItem>
                  <SelectItem value="em_processo_baixa">Em Processo de Baixa</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="revogada">Revogada</SelectItem>
                  {isAdmin && <SelectItem value="cancelado">Cancelado</SelectItem>}
                </SelectContent>
              </Select>

              {/* Payment Status - Admin Only */}
              {isAdmin && (
                <Select value={selectedPaymentStatus} onValueChange={setSelectedPaymentStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos pagamentos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos pagamentos</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="processando">Processando</SelectItem>
                    <SelectItem value="nao_pago">Não Pago</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Partner Filter - Admin Only */}
              {isAdmin && (
                <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os parceiros" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os parceiros</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.company_name ? `(${p.company_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Date Filters - Admin Only */}
              {isAdmin && (
                <>
                  <div>
                    <Input
                      type="date"
                      placeholder="Data início"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Input
                      type="date"
                      placeholder="Data fim"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* Clear Filters Button */}
              {isAdmin && (searchTerm || selectedPeriod !== 'all' || selectedStatus !== 'all' || selectedPaymentStatus !== 'all' || selectedPartner !== 'all' || startDate || endDate) && (
                <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground">
                  Limpar Filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Selected Actions - Admin Only */}
        {isAdmin && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="select-all-clients"
                  checked={selectedClients.length === paginatedClients.length && paginatedClients.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedClients(paginatedClients.map(c => c.id));
                    } else {
                      setSelectedClients([]);
                    }
                  }}
                  data-testid="select-all-clients-checkbox"
                />
                <label htmlFor="select-all-clients" className="text-sm font-medium cursor-pointer">
                  Selecionar Todos
                </label>
              </div>
              {selectedClients.length > 0 && (
                <Badge variant="secondary">{selectedClients.length} selecionado(s)</Badge>
              )}
            </div>
            {selectedClients.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowMovePeriodModal(true)} 
                  data-testid="move-period-btn"
                >
                  <FolderInput className="w-4 h-4 mr-2" />
                  Mover para Período
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowMigrateModal(true)} 
                  data-testid="migrate-partner-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Migrar para Parceiro
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Selecionados
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Clients Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : paginatedClients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhum cliente encontrado
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedClients.map((client, index) => (
              <ClientCard
                key={client.id}
                client={client}
                index={index}
                isAdmin={isAdmin}
                showCheckbox={isAdmin}
                isSelected={selectedClients.includes(client.id)}
                onSelect={(id) => {
                  setSelectedClients(prev => 
                    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                  );
                }}
                onClick={() => handleClientClick(client)}
                onEdit={(c) => handleEditClient(c)}
                onDelete={isAdmin ? handleDeleteClient : null}
                onManageStatus={isAdmin ? (c) => { setSelectedClientForStatus(c); setShowStatusDetailModal(true); } : null}
                onPaymentStatusChange={isAdmin ? handlePaymentStatusChange : null}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Modals */}
        {showAddModal && (
          <AddClientModal
            onClose={() => setShowAddModal(false)}
            onSuccess={() => { setShowAddModal(false); fetchData(); }}
            activePeriodId={activePeriod?.id}
            isAdmin={isAdmin}
          />
        )}
        {showEditModal && editingClient && (
          <EditClientModal
            client={editingClient}
            isAdmin={isAdmin}
            onClose={() => { setShowEditModal(false); setEditingClient(null); }}
            onSuccess={() => { setShowEditModal(false); setEditingClient(null); fetchData(); }}
          />
        )}
        {showBatchUploadModal && (
          <BatchUploadModal
            onClose={() => setShowBatchUploadModal(false)}
            onSuccess={() => { setShowBatchUploadModal(false); fetchData(); }}
            periodId={activePeriod?.id}
            isAdmin={isAdmin}
          />
        )}
        {showStatusDetailModal && selectedClientForStatus && (
          <StatusDetailModal
            client={selectedClientForStatus}
            onClose={() => { setShowStatusDetailModal(false); setSelectedClientForStatus(null); }}
            onSuccess={() => { setShowStatusDetailModal(false); setSelectedClientForStatus(null); fetchData(); }}
          />
        )}
        {showExportModal && (
          <ExportExcelModal
            onClose={() => setShowExportModal(false)}
            periods={periods}
          />
        )}
        {showMovePeriodModal && (
          <MoveToPeriodModal
            selectedClients={selectedClients}
            periods={periods}
            onClose={() => setShowMovePeriodModal(false)}
            onSuccess={() => { 
              setShowMovePeriodModal(false); 
              setSelectedClients([]); 
              fetchData(); 
            }}
          />
        )}
        {showMigrateModal && (
          <MigrateClientsModal
            isOpen={showMigrateModal}
            selectedClients={selectedClients}
            onClose={() => setShowMigrateModal(false)}
            onSuccess={() => { 
              setShowMigrateModal(false); 
              setSelectedClients([]); 
              fetchData(); 
            }}
          />
        )}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmModal}
          onClose={() => setShowDeleteConfirmModal(false)}
          onConfirm={executeDelete}
          title="Excluir Cliente(s)"
          description="Esta ação é irreversível. Todos os documentos e dados associados serão permanentemente excluídos."
          itemName={deleteTarget.name}
          itemType="Cliente"
          loading={deleteLoading}
        />
      </div>
    </Layout>
  );
};

export default ClientsPage;
